/**
 * Цена изделия по прайсу БЕЗ СЕРВЕРА.
 *
 * Онлайн цену считает бэкенд (`POST /orders/:id/calc-preview`), без сети замерщик
 * не видел цену вообще — а клиент спрашивает её на замере. Здесь тот же порядок
 * шагов, что в CalcService.computeItemFinancials + PriceBookPricingService.priceItem,
 * и те же формулы из `@jaluzi/calc/pricing` (сервер использует их же):
 *   ступень по площади/ширине → минималки → тариф × множитель → доплата типа
 *   механизма → мотор → перевод в сумы по курсу (зафиксированному на заказе или дня).
 *
 * Данные — закешированный offer и аксессуары прайса. Где сервер ответил бы ошибкой
 * (пустая ячейка, неполные диапазоны, неоднозначная доплата), возвращаем null:
 * лучше «цена после синхронизации», чем неверная цена клиенту. Сервер при отправке
 * всё равно пересчитает цену сам — это оценка для показа, не источник истины.
 */
import {
  MechanismSurchargeError,
  calculateMechanismSurchargeCents,
  pbLamellaSnapshot,
  pbSplitItemCents,
  pickPriceRule,
  priceRuleFactor,
  selectMechanismSurcharge,
  usdCentsToTiyin,
  type PbLamellaLayout,
} from '@jaluzi/calc/pricing';
import type { PbAccessory, PbOffer, PbOfferRow, PbOfferTable } from '../features/pricebook/queries.js';

export interface OfflinePriceItem {
  priceBook?: { tableId: string; rowId: string; optionKey: string; withoutCornice?: boolean; extraRowIds?: string[]; lamellaLayout?: PbLamellaLayout | null } | null;
  mechanismRowId?: string | null;
  mechanismTypeId?: string | null;
  /** Правка изделия: его сохранённый выбор. Как на сервере, скрытые позже тип
   *  механизма и карниз/тариф допустимы, только пока их не меняют. */
  retained?: OfflineRetainedSelection | null;
}

export interface OfflineRetainedSelection {
  mechanismTypeId?: string | null;
  priceBook?: { tableId: string; optionKey: string } | null;
}

export interface OfflinePriceContext {
  offer: PbOffer | undefined;
  accessories: readonly PbAccessory[] | undefined;
  /** Курс заказа, если зафиксирован (usdRateFixedAt) — иначе курс дня из offer. */
  orderRate?: { usdRate?: string | null; usdRateFixedAt?: string | null } | null;
}

export interface OfflinePrice {
  priceTiyin: bigint;
  priceUsdCents: number;
  columnLabel: string | null;
}

const hasPrice = (row: PbOfferRow, columnId: string) => {
  const p = row.cells[columnId]?.p;
  return typeof p === 'number' && Number.isFinite(p) && p > 0;
};

function locate(offer: PbOffer, tableId: string): { sectionCode: string; table: PbOfferTable } | null {
  for (const section of offer.sections) {
    const table = section.tables.find((t) => t.id === tableId);
    if (table) return { sectionCode: section.code, table };
  }
  return null;
}

/** Сохранённый выбор изделия, которое правят (для офлайн-оценки правки). */
export function retainedSelectionOf(item: {
  mechanismTypeId?: string | null; pbTableId?: string | null; pbOptionKey?: string | null;
} | null | undefined): OfflineRetainedSelection | null {
  return item ? {
    mechanismTypeId: item.mechanismTypeId ?? null,
    priceBook: item.pbTableId && item.pbOptionKey ? { tableId: item.pbTableId, optionKey: item.pbOptionKey } : null,
  } : null;
}

export function estimateOfflinePrice(
  item: OfflinePriceItem,
  size: { widthMm: number; heightMm: number; quantity: number },
  ctx: OfflinePriceContext,
): OfflinePrice | null {
  const pb = item.priceBook;
  if (!pb || !ctx.offer) return null;
  const { widthMm, heightMm, quantity } = size;
  if (!Number.isInteger(widthMm) || !Number.isInteger(heightMm) || widthMm < 1 || heightMm < 1
    || widthMm > 10000 || heightMm > 10000 || !Number.isInteger(quantity) || quantity < 1 || quantity > 999) return null;

  const located = locate(ctx.offer, pb.tableId);
  if (!located) return null;
  const { table, sectionCode } = located;
  const row = table.rows.find((r) => r.id === pb.rowId);
  const option = table.options.find((o) => o.key === pb.optionKey);
  if (!row || !option) return null;
  const keepsOption = item.retained?.priceBook?.tableId === pb.tableId
    && item.retained.priceBook.optionKey === pb.optionKey;
  // Как priceItem: в таблице «карниз × механизм» продаётся только связанный и
  // нескрытый тариф (скрытый — лишь у изделия, где он уже сохранён).
  if (table.options.some((o) => o.corniceId) && (!option.corniceId || (option.hidden && !keepsOption))) return null;
  const columnIdOf = (index: number) => table.columns.find((c) => c.index === index)?.id;

  // Те же проверки полноты тарифа, что в resolveCell: основная лента с ценами —
  // верхний допуск для цвета; диапазонная ветка продаётся только целиком.
  const required = [...new Set(option.columns.map((c) => c.columnIndex))]
    .map(columnIdOf).filter((id): id is string => !!id);
  const parentOf = (fabric: PbOfferRow) => fabric.parentRowId && fabric.parentRowId !== fabric.id
    ? table.rows.find((r) => r.id === fabric.parentRowId) ?? null
    : null;
  const parent = parentOf(row);
  // Изделие из нескольких тканей: каждая проверяется и считается, как одиночная.
  const extraIds = [...new Set(pb.extraRowIds ?? [])].filter((id) => id !== row.id);
  const extras = extraIds.map((id) => table.rows.find((r) => r.id === id));
  if (extras.some((r) => !r)) return null;
  const fabrics = [row, ...(extras as PbOfferRow[])];
  for (const fabric of fabrics) {
    const fabricParent = parentOf(fabric);
    const parentHasPrice = !!fabricParent && Object.values(fabricParent.cells).some((c) => typeof c?.p === 'number' && c.p > 0);
    for (const priceRow of fabricParent && parentHasPrice ? [fabricParent, fabric] : [fabric]) {
      const missing = required.filter((id) => !hasPrice(priceRow, id)).length;
      if (required.length > 0 && missing === required.length) return null;
      if (required.length > 1 && missing > 0) return null;
    }
  }

  const widthM = widthMm / 1000;
  const rule = pickPriceRule(option.columns, widthM * (heightMm / 1000), widthM);
  if (!rule) return null;
  const columnId = columnIdOf(rule.columnIndex);
  if (!columnId) return null;
  const unitCents = fabrics.map((fabric) => fabric.cells[columnId]?.p);
  if (unitCents.some((p) => typeof p !== 'number' || p <= 0)) return null;
  const { qtyFactor } = priceRuleFactor(rule, widthMm, heightMm);
  // Метраж делится между тканями поровну (или по ламелям раскладки двух тканей) —
  // как resolveCells на сервере.
  const lamella = pb.lamellaLayout && fabrics.length === 2 ? pbLamellaSnapshot(pb.lamellaLayout, widthMm) : null;
  const baseCents = pbSplitItemCents(unitCents as number[], qtyFactor, lamella?.counts)
    .reduce((sum, cents) => sum + cents, 0) * quantity;

  // Тип механизма: доплата по правилу «Механизмы» (AUTO-строки), мотор — только у MOTORS.
  const accessories = ctx.accessories ?? [];
  let mechanismRowId = item.mechanismRowId ?? null;
  let surchargeCents = 0;
  if (item.mechanismTypeId) {
    // Как computeItemFinancials: скрытый тип — только сохранённый у изделия;
    // вне allow-list ветки — только если и карниз-опция та же.
    const keepsType = item.mechanismTypeId === item.retained?.mechanismTypeId;
    const type = ctx.offer.mechanismTypes?.find((t) => t.id === item.mechanismTypeId && (!t.hidden || keepsType));
    if (!type) return null;
    if (option.mechanismTypeIds?.length && !(keepsType && keepsOption)
      && !option.mechanismTypeIds.includes(type.id)) return null;
    if (type.kind !== 'MOTORS') mechanismRowId = null;
    const materialRowLogicalId = parent?.logicalId ?? parent?.id ?? row.logicalId ?? row.id;
    try {
      const surcharge = selectMechanismSurcharge(
        accessories
          // Как resolveMechanismSurcharge: только AUTO-строки раздела «Механизмы».
          .filter((a) => a.mechanismPriceMode === 'AUTO' && a.mechanismTypeId && a.sectionCode === 'MECHANISMS')
          .map((a) => ({
            rowId: a.rowId,
            mechanismTypeId: a.mechanismTypeId!,
            priceCents: a.priceCents !== undefined ? a.priceCents : a.priceUsdCents,
            unit: a.mechanismPriceUnit ?? 'PER_ITEM',
            sectionCodes: a.mechanismSectionCodes ?? [],
            targetRowLogicalIds: a.mechanismTargetRowLogicalIds ?? [],
          })),
        { mechanismTypeId: type.id, sectionCode, materialRowLogicalId },
      );
      surchargeCents = calculateMechanismSurchargeCents(surcharge, {
        widthMm, heightMm, quantity, baseUnit: rule.unit, baseQtyFactor: qtyFactor,
      });
    } catch (e) {
      if (e instanceof MechanismSurchargeError) return null;
      throw e;
    }
  }
  let motorCents = 0;
  if (mechanismRowId) {
    const motor = accessories.find((a) => a.rowId === mechanismRowId);
    // Нет мотора в кеше — сервер его найдёт, а мы не знаем цену: не угадываем.
    if (!motor || motor.mechanismPriceMode === 'AUTO') return null;
    if (item.mechanismTypeId && motor.mechanismTypeId && motor.mechanismTypeId !== item.mechanismTypeId) return null;
    motorCents = (motor.priceUsdCents ?? 0) * quantity;
  }

  const fixed = ctx.orderRate?.usdRateFixedAt && ctx.orderRate.usdRate ? Number(ctx.orderRate.usdRate) : null;
  const rate = fixed ?? ctx.offer.rate?.value ?? null;
  if (!rate || !Number.isFinite(rate)) return null;
  return {
    // Как на сервере: каждая часть переводится в сумы отдельно, потом складывается.
    priceTiyin: usdCentsToTiyin(baseCents, rate) + usdCentsToTiyin(surchargeCents, rate) + usdCentsToTiyin(motorCents, rate),
    priceUsdCents: baseCents + surchargeCents + motorCents,
    columnLabel: table.columns.find((c) => c.id === columnId)?.label ?? null,
  };
}
